import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import AppSidebar from './components/AppSidebar';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import SetupGate from './components/SetupGate';
import SetupWizard from './components/SetupWizard';
import { AuthProvider } from './components/AuthContext';
import AssetsList from './components/AssetsList';
import AssetDetails from './components/AssetDetails';
import WorkOrders from './components/WorkOrderList';
import WorkOrderDetails from './components/WorkOrderDetails';
import CustomModelList from './components/CustomModelList';
import CustomModelForm from './components/CustomModelForm';
import CustomAppList from './components/CustomAppList';
import CustomAppStudio from './components/CustomAppStudio';
import ExtendScreenList from './components/ExtendScreenList';
import ExtendScreenStudio from './components/ExtendScreenStudio';
import CustomActionScriptList from './components/CustomActionScriptList';
import CustomActionScriptStudio from './components/CustomActionScriptStudio';
import Organizations from './components/Organizations';
import OrganizationDetail from './components/OrganizationDetails';
import DomainsList from './components/DomainsList';
import SynonymDetail from './components/SynonymDetail';
import FieldConfig from './components/FieldConfig';
import Classifications from './components/ClassificationList';
import ClassificationDetail from './components/ClassificationDetail';
import Meters from './components/MeterList';
import MeterDetails from './components/MeterDetails';
import Locations from './components/LocationList';
import LocationDetails from './components/LocationDetails';
import Vendors from './components/VendorList';
import VendorDetails from './components/VendorDetails';
import COBieManager from './components/COBieManager';
import Crafts from './components/CraftList';
import CraftDetail from './components/CraftDetails';
import Storerooms from './components/StoreroomList';
import StoreroomDetail from './components/StoreroomDetails';
import Items from './components/ItemList';
import ItemDetail from './components/ItemDetails';
import InventoryUsage from './components/InventoryUsage';
import InventoryUsages from './components/InventoryUsageList';
import Materials from './components/MaterialRequestList';
import MaterialRequest from './components/MaterialRequest';
import PR from './components/PRList';
import PRDetail from './components/PRDetails';
import PO from './components/POList';
import PODetail from './components/PODetails';
import InvoiceList from './components/InvoiceList';
import InvoiceDetails from './components/InvoiceDetails';
import GLAccountList from './components/GLAccountList';
import GLAccountDetails from './components/GLAccountDetails';
import GLEntryList from './components/GLEntryList';
import TrialBalance from './components/TrialBalance';
import GLLedger from './components/GLLedger';
import VendorStatement from './components/VendorStatement';
import Receiving from './components/ReceivingList';
import ReceivingDetail from './components/ReceivingDetails';
import Inventory from './components/InventoryList';
import InventoryDetails from './components/InventoryDetails';
import Labors from './components/LaborList';
import LaborDetails from './components/LaborDetails';
import Persons from './components/PersonList';
import PersonDetails from './components/PersonDetails';
import UniversalImporter from './components/UniversalImporter';
import JP from './components/JPList';
import JPDetails from './components/JPDetails';
import PM from './components/PMList';
import PMDetails from './components/PMDetails';
import Users from './components/UserList';
import UserDetails from './components/UserDetails';
import Roles from './components/RoleList';
import RoleDetails from './components/RoleDetails';
import ReportDesigner from './components/ReportDesigner';
import ReportList from './components/ReportList';
import { FontSizeProvider } from './components/FontSizeContext';
import ReportWizard from './components/ReportWizard';
import SafetyPlans from './components/SafetyPlanList';
import SafetyPlanDetails from './components/SafetyPlanDetails';
import Hazards  from './components/HazardList';
import HazardDetails from './components/HazardDetails';
import Tagouts from './components/TagoutList';
import TagoutDetails from './components/TagoutDetails';
import Precautions from './components/PrecautionList';
import PrecautionDetails from './components/PrecautionDetails';
import WorkflowDesigner from './components/WorkflowDesigner';
import WorkflowList from './components/WorkflowList';
import ActionScriptList from './components/ActionScriptList';
import ActionScriptStudio from './components/ActionScriptStudio';
import SRList from './components/SRList';
import SRDetails from './components/SRDetails';
import IncidentList from './components/IncidentList';
import IncidentDetails from './components/IncidentDetails';
import FailureCodeList from './components/FailureCodesList';
import FailureCodeDetails from './components/FailureCodesDetails';
import { useDispatch } from 'react-redux';
import { incrementNotificationCount } from './redux/notificationsSlice';
import AssetCategoryList from './components/AssetCategoryList';
import AssetCategoryDetails from './components/AssetCategoryDetails';
import toast, { Toaster } from 'react-hot-toast';
import BackupManager from './components/BackupManager';
import ToolList from './components/ToolList';
import ToolDetails from './components/ToolDetails';
import RFQList from './components/RFQList';
import RFQDetails from './components/RFQDetails';
import ContractList from './components/ContractList';
import ContractDetails from './components/ContractDetails';
import ContractTermTemplateList from './components/ContractTermTemplateList';
import SLAList from './components/SLAList';
import SLADetails from './components/SLADetails';
import { SidebarProvider } from './components/SidebarContext';
import SmartScheduler from './components/SmartScheduler';
import IoTMonitoring from './components/IoTMonitoring';
import FeatureMaster from './components/FeatureMaster';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { ThemeProvider, useTheme } from './components/ThemeContext';

// توحيد ثيم Ant Design مع هوية Teknora بدل اللون الافتراضي #1677ff.
// القيم بتتقرأ من الـ CSS variables المحسوبة (resolvedTokens من ThemeContext)
// بدل خريطة ثابتة، عشان تتبع أي حزمة ألوان (palette) مختارة مش بس light/dark.
const FALLBACK_TOKENS = {
  colorPrimary: '#005495', colorInfo: '#005495', colorSuccess: '#16a34a',
  colorWarning: '#f59e0b', colorError: '#dc2626',
};

const AntdThemeBridge = ({ children }) => {
  const { mode, resolvedTokens } = useTheme();
  return (
    <ConfigProvider
      theme={{
        token: { ...(resolvedTokens || FALLBACK_TOKENS), borderRadius: 6, fontFamily: 'inherit' },
        algorithm: mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      }}
    >
      {children}
    </ConfigProvider>
  );
};

// توحيد شكل الـ toast (react-hot-toast) مع الثيم الحالي — نداءات toast.* نفسها
// مبعثرة في 90+ ملف، فبنوحّد المظهر من مكان واحد (الـ Toaster provider) بدل
// ما نلمس كل نداء لوحده.
const ThemedToaster = () => {
  const { resolvedTokens } = useTheme();
  return (
    <Toaster
      position="top-center"
      reverseOrder={false}
      toastOptions={{
        style: {
          background: resolvedTokens?.colorBgContainer || '#ffffff',
          color: resolvedTokens?.colorText || '#1a2332',
          border: `1px solid ${resolvedTokens?.colorBorder || '#e5e7eb'}`,
          fontWeight: 700,
        },
        success: { iconTheme: { primary: resolvedTokens?.colorSuccess || '#16a34a', secondary: '#fff' } },
        error: { iconTheme: { primary: resolvedTokens?.colorError || '#dc2626', secondary: '#fff' } },
      }}
    />
  );
};
// داخل الـ Routes



// مكوّن الـ Layout الخاص بالداشبورد فقط
const DashboardLayout = ({ children }) => (
  <div className="flex flex-col h-screen overflow-hidden">
    <Header />
    <div className="flex flex-1 overflow-hidden">
      <Sidebar /> {/* المكون القديم للرئيسية فقط */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden bg-surface-app">{children}</main>
    </div>
  </div>
);

// مكوّن الـ Layout الخاص بالتطبيقات (Organizations, Assets, etc.)
const AppLayout = ({ children, appName, viewType }) => (
  <div className="flex flex-col h-screen overflow-hidden">
    <Header />
    <div className="flex flex-1 overflow-hidden">
      <AppSidebar appName={appName} viewType={viewType} /> {/* المكون الديناميكي */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden bg-surface-app">{children}</main>
    </div>
  </div>
);

// 🎯 حارس ديناميكي لأي تطبيق كستم - ProtectedRoute بياخد module ثابت لكل
// Route عادةً، لكن هنا الـ module (custom_<slug>) مش معروف إلا وقت التشغيل
// من الرابط نفسه - فبنقراه بـ useParams ونمرره جوه بدل ما نغيّر ProtectedRoute
// نفسه (اللي باقي الراوتس التابتة كلها معتمدة على شكله الحالي).
const CustomAppGuard = ({ mode }) => {
  const { slug } = useParams();
  return (
    <ProtectedRoute module={`custom_${slug}`}>
      <AppLayout appName={`CUSTOM_${(slug || '').toUpperCase()}`} viewType={mode === 'list' ? 'ListView' : 'DetailView'}>
        {mode === 'list' ? <CustomModelList slug={slug} /> : <CustomModelForm slug={slug} />}
      </AppLayout>
    </ProtectedRoute>
  );
};

function App() {
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const { i18n } = useTranslation();
  //const dispatch = useDispatch();

  useEffect(() => {
  // 1. تحديد الاتجاه بناءً على اللغة الحالية
  const currentLang = i18n.language || 'en';
  // اللغات اللي بتدعم الـ RTL (العربية والأردية)
  const isRtl = ['ar', 'ur'].includes(currentLang);
  const dir = isRtl ? 'rtl' : 'ltr';

  // 2. تحديث الـ DOM الأساسي
  document.documentElement.dir = dir;
  document.documentElement.lang = currentLang;

  // 3. (إختياري) حفظ الاتجاه في الـ Body كمان للأمان
  document.body.dir = dir;

  // 4. ضبط الـ Font-Family بناءً على اللغة (لو عاوز العربي يبقى Cairo دايماً)
  if (isRtl) {
    document.body.style.fontFamily = "'Cairo', sans-serif";
  } else {
    document.body.style.fontFamily = "'Inter', sans-serif"; // أو أي خط إنجليزي
  }

  console.log(`🌐 Layout switched to: ${dir} (${currentLang})`);
}, [i18n.language]);

  useEffect(() => {
    // 1. استرجاع القيم المحفوظة أو استخدام الافتراضي
    const savedLng = localStorage.getItem('i18nextLng') || 'ar';
    const savedDir = localStorage.getItem('layoutDir') || (savedLng === 'ar' ? 'rtl' : 'ltr');

    // 2. تطبيق اللغة على i18next
    if (i18n.language !== savedLng) {
      i18n.changeLanguage(savedLng);
    }

    // 3. تطبيق الاتجاه والخط بشكل نهائي
    document.documentElement.dir = savedDir;
    document.body.dir = savedDir;
    
    // تطبيق الخط العربي Cairo لو اللغة عربية
    if (savedLng === 'ar' || savedLng === 'ur') {
      document.body.style.fontFamily = "'Cairo', sans-serif";
    } else {
      document.body.style.fontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
    }

  }, [i18n.language]);

  useEffect(() => {
    if (!session || !session.username) {
      console.log("⚠️ WebSocket: Username not found in session yet.");
      return; 
    }
    
    // 1. بنشوف الموقع شغال بـ https ولا http عشان نحدد الـ WebSocket هيبقى wss ولا ws
    const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
    
    // 2. بنسحب الدومين الحالي (بالبورت بتاعه لو موجود) من المتصفح
    const currentHost = window.location.host;
    
    // 3. بنبني الرابط الديناميكي
    const wsUrl = `${wsProtocol}${currentHost}/ws/${session.username}`;
    
    // 4. نفتح الاتصال
    const socket = new WebSocket(wsUrl);

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // استخدام react-hot-toast لإظهار الإشعار فورا
      toast(data.message, {
        icon: data.type === 'success' ? '✅' : '🔔',
        style: { borderRadius: '10px', background: '#1a3353', color: '#fff', fontWeight: 'bold' }
      });
      
      // ممكن كمان تحدث الـ Redux عشان رقم الإشعارات في الجرس يزيد
      //dispatch(incrementNotificationCount());
    };

  return () => socket.close();
}, [session]);

  return (
  <ThemeProvider>
  <AntdThemeBridge>
  <AuthProvider>
    <FontSizeProvider>
    <SidebarProvider>
    <Router>
      <ThemedToaster />
      <SetupGate>
      <Routes>
        <Route path="/login" element={<Login />} />

        {/* 🎯 معالج الإعداد الأول - محتاج يوزر مسجل دخول بس، من غير أي فحص
            صلاحية موديول (شوف ProtectedRoute.js: الفحص بيتخطى لو module مش
            متبعت). SetupGate فوق هي اللي بتحوّل الناس هنا لما يكون لازم */}
        <Route path="/setup" element={
          <ProtectedRoute>
            <SetupWizard />
          </ProtectedRoute>
        } />

        {/* شاشة الصفحة الرئيسية - بدون Sidebar خاص أو بـ Sidebar عام */}
        <Route path="/dashboard" element={
          <DashboardLayout>
            <Dashboard />
          </DashboardLayout>
        } />    
        {/* شاشة SmartScheduler */}
        <Route path="/smartscheduler" element={
          <ProtectedRoute module="smartscheduler">
            <AppLayout appName="SMARTSCHEDULER" viewType="DetailView">
              <SmartScheduler />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* شاشة مراقبة إنترنت الأشياء (IoT) - إمبيد داشبورد ThingsBoard */}
        <Route path="/iot" element={
          <ProtectedRoute module="iot">
            <AppLayout appName="IOT" viewType="DetailView">
              <IoTMonitoring />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* مسارات الدومينات (المرادفات) */}
        <Route path="/domains" element={
          <ProtectedRoute module="domains">
            <AppLayout appName="DOMAINS" viewType="ListView">
              <DomainsList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/backups" element={
          <ProtectedRoute module="backups">
            <AppLayout appName="BACKUP" viewType="DetailView">
              <BackupManager />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/features" element={
          <ProtectedRoute module="features">
          <AppLayout appName="FEATURE" viewType="DetailView">
           <FeatureMaster/>
           </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/cobiemanager" element={
          <ProtectedRoute module="cobiemanager">
            <AppLayout appName="COBIE" viewType="DetailView">
              <COBieManager />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/reportwizard" element={
          <ProtectedRoute module="reportwizard">
            <AppLayout appName="REPORTWIZARD" viewType="DetailView">
              <ReportWizard />
            </AppLayout>
          </ProtectedRoute>
        } />
        /** مسارات محرك الـ Workflow */ 
        <Route path="/workflow/new" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="DetailView">
              <WorkflowDesigner />
            </AppLayout>
          </ProtectedRoute>
        } /> 
        <Route path="/workflow" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="ListView">
              <WorkflowList />
            </AppLayout>
          </ProtectedRoute>
        } /> 
        <Route path="/workflow/:id" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="DetailView">
              <WorkflowDesigner />
            </AppLayout>
          </ProtectedRoute>
        } />
        /** Action Script */
        <Route path="/admin/workflow/studio/new" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="DetailView">
              <ActionScriptStudio />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/workflow/studio" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="ListView">
              <ActionScriptList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/workflow/studio/:id" element={
          <ProtectedRoute module="workflow">
            <AppLayout appName="WORKFLOW" viewType="DetailView">
              <ActionScriptStudio />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* استوديو التطبيقات المخصصة - Custom Application Builder (أدمن) */}
        <Route path="/admin/custom-apps" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="ListView">
              <CustomAppList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/custom-apps/:slug/studio" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="DetailView">
              <CustomAppStudio />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/custom-apps/extend" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="ListView">
              <ExtendScreenList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/custom-apps/extend/:screenKey" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="DetailView">
              <ExtendScreenStudio />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/custom-apps/:slug/scripts" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="ListView">
              <CustomActionScriptList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/custom-apps/:slug/scripts/:scriptId" element={
          <ProtectedRoute module="custom_apps" action="write">
            <AppLayout appName="CUSTOMAPPS" viewType="DetailView">
              <CustomActionScriptStudio />
            </AppLayout>
          </ProtectedRoute>
        } />

        /* مسارات تقارير الـ BI */
        <Route path="/reportdesigner/new" element={
          <ProtectedRoute module="reportdesigner">
            <AppLayout appName="REPDESIGNER" viewType="DetailView">
              <ReportDesigner />
            </AppLayout>
          </ProtectedRoute>
        } /> 
        <Route path="/reportdesigner/:id" element={
          <ProtectedRoute module="reportdesigner">
            <AppLayout appName="REPDESIGNER" viewType="DetailView">
              <ReportDesigner />
            </AppLayout>
          </ProtectedRoute>
        } />         
        <Route path="/reportlist" element={
          <ProtectedRoute module="reportlist">
            <AppLayout appName="REPORTLIST" viewType="ListView">
              <ReportList />
            </AppLayout>
          </ProtectedRoute>
        } />          
        <Route path="/uniimporter" element={
          <ProtectedRoute  module="uniimporter">
            <AppLayout appName="UIMPORTER" viewType="DetailView">
              <UniversalImporter />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/admin/field-config" element={
          <ProtectedRoute module="field-config">
            <AppLayout appName="FILED_CONFIG" viewType="DetailView">
              <FieldConfig />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/domains/new" element={
          <ProtectedRoute module="domains">
            <AppLayout appName="DOMAINS" viewType="DetailView">        
              <SynonymDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/domains/:id" element={
          <ProtectedRoute module="domains">
            <AppLayout appName="DOMAINS" viewType="DetailView">        
              <SynonymDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Asset Categories */}

        <Route path="/asset-categories/new" element={
          <ProtectedRoute module="asset-categories">
            <AppLayout appName="ASSET_CATEGORIES" viewType="DetailView">        
              <AssetCategoryDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/asset-categories/:id" element={
          <ProtectedRoute  module="asset-categories">
            <AppLayout appName="ASSET_CATEGORIES" viewType="DetailView">        
              <AssetCategoryDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/asset-categories" element={
          <ProtectedRoute  module="asset-categories">
            <AppLayout appName="ASSET_CATEGORIES" viewType="ListView">
              <AssetCategoryList />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* GL / Accounting */}
        <Route path="/gl-accounts/new" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="GL_ACCOUNTS" viewType="DetailView">
              <GLAccountDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/gl-accounts/:id" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="GL_ACCOUNTS" viewType="DetailView">
              <GLAccountDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/gl-accounts" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="GL_ACCOUNTS" viewType="ListView">
              <GLAccountList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/gl-entries" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="GL_ENTRIES" viewType="ListView">
              <GLEntryList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/trial-balance" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="TRIAL_BALANCE" viewType="ListView">
              <TrialBalance />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/gl-ledger" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="GL_LEDGER" viewType="ListView">
              <GLLedger />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/vendor-statement" element={
          <ProtectedRoute module="gl-accounts">
            <AppLayout appName="VENDOR_STATEMENT" viewType="ListView">
              <VendorStatement />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Failure Codes */}

        <Route path="/failure-codes/new" element={
          <ProtectedRoute module="failure-codes">
            <AppLayout appName="FAILURE_CODES" viewType="DetailView">        
              <FailureCodeDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/failure-codes/:id" element={
          <ProtectedRoute  module="failure-codes">
            <AppLayout appName="FAILURE_CODES" viewType="DetailView">        
              <FailureCodeDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/failure-codes" element={
          <ProtectedRoute  module="failure-codes">
            <AppLayout appName="FAILURE_CODES" viewType="ListView">
              <FailureCodeList />
            </AppLayout>
          </ProtectedRoute>
        } />          
        {/* INCIDENTS */}

        <Route path="/incidents/new" element={
          <ProtectedRoute module="incidents">
            <AppLayout appName="INCIDENTS" viewType="DetailView">        
              <IncidentDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/incidents/:id" element={
          <ProtectedRoute  module="incidents">
            <AppLayout appName="INCIDENTS" viewType="DetailView">        
              <IncidentDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/incidents" element={
          <ProtectedRoute  module="incidents">
            <AppLayout appName="INCIDENTS" viewType="ListView">
              <IncidentList />
            </AppLayout>
          </ProtectedRoute>
        } />                   
        {/* RECEIVING */}

        <Route path="/receiving/:ponum" element={
          <ProtectedRoute module="receiving">
            <AppLayout appName="RECEIVING" viewType="DetailView">        
              <ReceivingDetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/receiving" element={
          <ProtectedRoute module="receiving">
            <AppLayout appName="RECEIVING" viewType="ListView">
              <Receiving />
            </AppLayout>
          </ProtectedRoute>
        } />     
        {/* HAZARDS */}

        <Route path="/hazards/new" element={
          <ProtectedRoute module="hazards">
            <AppLayout appName="HAZARD" viewType="DetailView">        
              <HazardDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/hazards/:id" element={
          <ProtectedRoute  module="hazards">
            <AppLayout appName="HAZARD" viewType="DetailView">        
              <HazardDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/hazards" element={
          <ProtectedRoute  module="hazards">
            <AppLayout appName="HAZARD" viewType="ListView">
              <Hazards />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* PRECAUTIONS */}

        <Route path="/precautions/new" element={
          <ProtectedRoute module="precautions">
            <AppLayout appName="PRECAUTIONS" viewType="DetailView">        
              <PrecautionDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/precautions/:id" element={
          <ProtectedRoute  module="precautions">
            <AppLayout appName="PRECAUTIONS" viewType="DetailView">        
              <PrecautionDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/precautions" element={
          <ProtectedRoute  module="precautions">
            <AppLayout appName="PRECAUTIONS" viewType="ListView">
              <Precautions />
            </AppLayout>
          </ProtectedRoute>
        } />        
        {/* TAGOUT */}

        <Route path="/tagouts/new" element={
          <ProtectedRoute module="tagouts">
            <AppLayout appName="TAGOUTS" viewType="DetailView">        
              <TagoutDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/tagouts/:id" element={
          <ProtectedRoute  module="tagouts">
            <AppLayout appName="TAGOUTS" viewType="DetailView">        
              <TagoutDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/tagouts" element={
          <ProtectedRoute  module="tagouts">
            <AppLayout appName="TAGOUTS" viewType="ListView">
              <Tagouts />
            </AppLayout>
          </ProtectedRoute>
        } />     
        {/* SAFETY */}

        <Route path="/safety/new" element={
          <ProtectedRoute module="safety">
            <AppLayout appName="SAFETY" viewType="DetailView">        
              <SafetyPlanDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/safety/:id" element={
          <ProtectedRoute  module="safety">
            <AppLayout appName="SAFETY" viewType="DetailView">        
              <SafetyPlanDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/safety" element={
          <ProtectedRoute  module="safety">
            <AppLayout appName="SAFETY" viewType="ListView">
              <SafetyPlans />
            </AppLayout>
          </ProtectedRoute>
        } />                      
        {/* PERSONS */}

        <Route path="/persons/new" element={
          <ProtectedRoute module="person">
            <AppLayout appName="PERSON" viewType="DetailView">        
              <PersonDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/persons/:id" element={
          <ProtectedRoute  module="person">
            <AppLayout appName="PERSON" viewType="DetailView">        
              <PersonDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/persons" element={
          <ProtectedRoute  module="person">
            <AppLayout appName="PERSON" viewType="ListView">
              <Persons />
            </AppLayout>
          </ProtectedRoute>
        } />              
        {/* LABORS */}

        <Route path="/labors/new" element={
          <ProtectedRoute  module="labor">
            <AppLayout appName="LABOR" viewType="DetailView">        
              <LaborDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/labors/:id" element={
          <ProtectedRoute  module="labor">
            <AppLayout appName="LABOR" viewType="DetailView">        
              <LaborDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/labors" element={
          <ProtectedRoute  module="labor">
            <AppLayout appName="LABOR" viewType="ListView">
              <Labors />
            </AppLayout>
          </ProtectedRoute>
        } />        
        {/* PO */}

        <Route path="/po/new" element={
          <ProtectedRoute  module="po">
            <AppLayout appName="PO" viewType="DetailView">        
              <PODetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/po/:id" element={
          <ProtectedRoute module="po">
            <AppLayout appName="PO" viewType="DetailView">        
              <PODetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/po" element={
          <ProtectedRoute module="po">
            <AppLayout appName="PO" viewType="ListView">
              <PO />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* Purchase Invoices */}
        <Route path="/invoices/new" element={
          <ProtectedRoute module="invoices">
            <AppLayout appName="INVOICE" viewType="DetailView">
              <InvoiceDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/invoices/:id" element={
          <ProtectedRoute module="invoices">
            <AppLayout appName="INVOICE" viewType="DetailView">
              <InvoiceDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/invoices" element={
          <ProtectedRoute module="invoices">
            <AppLayout appName="INVOICE" viewType="ListView">
              <InvoiceList />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* PR */}

        <Route path="/pr/new" element={
          <ProtectedRoute module="pr">
            <AppLayout appName="PR" viewType="DetailView">        
              <PRDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/pr/:id" element={
          <ProtectedRoute module="pr">
            <AppLayout appName="PR" viewType="DetailView">        
              <PRDetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/pr" element={
          <ProtectedRoute module="pr">
            <AppLayout appName="PR" viewType="ListView">
              <PR />
            </AppLayout>
          </ProtectedRoute>
        } />              
        {/* RFQ*/}
        <Route path="/rfq/new" element={
          <ProtectedRoute module="rfq">
            <AppLayout appName="RFQ" viewType="DetailView">        
              <RFQDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/rfq/:id" element={
          <ProtectedRoute module="rfq">
            <AppLayout appName="RFQ" viewType="DetailView">        
              <RFQDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/rfq" element={
          <ProtectedRoute module="rfq">
            <AppLayout appName="RFQ" viewType="ListView">
              <RFQList />
            </AppLayout>
          </ProtectedRoute>
        } />    
        {/* Contracts*/}
        <Route path="/contracts/new" element={
          <ProtectedRoute module="contracts">
            <AppLayout appName="CONTRACT" viewType="DetailView">        
              <ContractDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/contracts/:id" element={
          <ProtectedRoute module="contracts">
            <AppLayout appName="CONTRACT" viewType="DetailView">        
              <ContractDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/contracts" element={
          <ProtectedRoute module="contracts">
            <AppLayout appName="CONTRACT" viewType="ListView">
              <ContractList />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/contract-term-templates" element={
          <ProtectedRoute module="contracts">
            <AppLayout appName="CONTRACT_TERM_TEMPLATES" viewType="ListView">
              <ContractTermTemplateList />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* SLA*/}
        <Route path="/sla/new" element={
          <ProtectedRoute module="sla">
            <AppLayout appName="SLA" viewType="DetailView">        
              <SLADetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/sla/:id" element={
          <ProtectedRoute module="sla">
            <AppLayout appName="SLA" viewType="DetailView">        
              <SLADetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/sla" element={
          <ProtectedRoute module="sla">
            <AppLayout appName="SLA" viewType="ListView">
              <SLAList />
            </AppLayout>
          </ProtectedRoute>
        } />          
        {/* Crafts */}
        <Route path="/crafts/new" element={
          <ProtectedRoute  module="craft">
            <AppLayout appName="CRAFTS" viewType="DetailView">        
              <CraftDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/crafts/:id" element={
          <ProtectedRoute  module="craft">
            <AppLayout appName="CRAFTS" viewType="DetailView">        
              <CraftDetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/crafts" element={
          <ProtectedRoute  module="craft">
            <AppLayout appName="CRAFTS" viewType="ListView">
              <Crafts />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Items */}
        <Route path="/items/new" element={
          <ProtectedRoute  module="items">
            <AppLayout appName="ITEMS" viewType="DetailView">        
              <ItemDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/items/:id" element={
          <ProtectedRoute  module="items">
            <AppLayout appName="ITEMS" viewType="DetailView">        
              <ItemDetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/items" element={
          <ProtectedRoute  module="items">
            <AppLayout appName="ITEMS" viewType="ListView">
              <Items />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* Tools */}
        <Route path="/tools/new" element={
          <ProtectedRoute  module="tools">
            <AppLayout appName="TOOLS" viewType="DetailView">        
              <ToolDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/tools/:id" element={
          <ProtectedRoute  module="tools">
            <AppLayout appName="TOOLS" viewType="DetailView">        
              <ToolDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/tools" element={
          <ProtectedRoute  module="tools">
            <AppLayout appName="TOOLS" viewType="ListView">
              <ToolList />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* شاشة طلبات الخدمة - MRs */}
        <Route path="/mr/new" element={
          <ProtectedRoute module="mr">
            <AppLayout appName="MR" viewType="DetailView">        
              <MaterialRequest />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/mr/:id" element={
          <ProtectedRoute module="mr">
            <AppLayout appName="MR" viewType="DetailView">        
              <MaterialRequest />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/mr" element={
          <ProtectedRoute module="mr">
            <AppLayout appName="MR" viewType="ListView">
              <Materials />
            </AppLayout>
          </ProtectedRoute>
        } />              
        {/* شاشة طلبات الخدمة - SRs */}
        <Route path="/sr/new" element={
          <ProtectedRoute module="sr">
            <AppLayout appName="SR" viewType="DetailView">        
              <SRDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/sr/:id" element={
          <ProtectedRoute module="sr">
            <AppLayout appName="SR" viewType="DetailView">        
              <SRDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/sr" element={
          <ProtectedRoute module="sr">
            <AppLayout appName="SR" viewType="ListView">
              <SRList />
            </AppLayout>
          </ProtectedRoute>
        } />                      
        <Route path="/inventoryusage/new" element={
          <ProtectedRoute module="inventoryusage">
            <AppLayout appName="INVENTORYUSAGE" viewType="DetailView">        
              <InventoryUsage />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventoryusage/:id" element={
          <ProtectedRoute module="inventoryusage">
            <AppLayout appName="INVENTORYUSAGE" viewType="DetailView">        
              <InventoryUsage />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/inventoryusage" element={
          <ProtectedRoute module="inventoryusage">
            <AppLayout appName="INVENTORYUSAGE" viewType="ListView">
              <InventoryUsages />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/inventory/:itemnum/:location" element={
          <ProtectedRoute module="inventory">
            <AppLayout appName="INVENTORY" viewType="DetailView">        
              <InventoryDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/inventory" element={
          <ProtectedRoute module="inventory">
            <AppLayout appName="INVENTORY" viewType="ListView">
              <Inventory />
            </AppLayout>
          </ProtectedRoute>
        } />                
        <Route path="/storerooms/new" element={
          <ProtectedRoute module="storerooms">
            <AppLayout appName="STOREROOMS" viewType="DetailView">        
              <StoreroomDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/storerooms/:id" element={
          <ProtectedRoute module="storerooms">
            <AppLayout appName="STOREROOMS" viewType="DetailView">        
              <StoreroomDetail />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/storerooms" element={
          <ProtectedRoute module="storerooms">
            <AppLayout appName="STOREROOMS" viewType="ListView">
              <Storerooms />
            </AppLayout>
          </ProtectedRoute>
        } />        
        <Route path="/vendors/new" element={
          <ProtectedRoute module="vendors">
            <AppLayout appName="VENDORS" viewType="DetailView">        
              <VendorDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/vendors/:id" element={
          <ProtectedRoute module="vendors">
            <AppLayout appName="VENDORS" viewType="DetailView">        
              <VendorDetails />
            </AppLayout>
          </ProtectedRoute>
        } />            
        <Route path="/vendors" element={
          <ProtectedRoute module="vendors">
            <AppLayout appName="VENDORS" viewType="ListView">
              <Vendors />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* شاشة المؤسسات - نحدد لها AppName */}
        {/* ملحوظة: الـ viewType يمكن إدارته داخلياً في المكون أو تمريره هنا */}
        <Route path="/organizations" element={
          <ProtectedRoute module="organizations">
            <AppLayout appName="ORGANIZATION" viewType="ListView">
              <Organizations />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* مسار إضافة منظمة جديدة */}
        <Route path="/organizations/new" element={
          <ProtectedRoute module="organizations">
            <AppLayout appName="ORGANIZATION" viewType="DetailView">
              <OrganizationDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* مسار إضافة منظمة جديدة */}
        <Route path="/organizations/:id" element={
          <ProtectedRoute module="organizations">
            <AppLayout appName="ORGANIZATION" viewType="DetailView">
              <OrganizationDetail />
            </AppLayout>
          </ProtectedRoute>
        } />        
        <Route path="/classifications" element={
          <ProtectedRoute module="classifications">
            <AppLayout appName="CLASSIFICATION" viewType="ListView">
              <Classifications />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* مسار إضافة منظمة جديدة */}
        <Route path="/classifications/new" element={
          <ProtectedRoute module="classifications">
            <AppLayout appName="CLASSIFICATION" viewType="DetailView">
              <ClassificationDetail />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* مسار إضافة منظمة جديدة */}
        <Route path="/classifications/:id" element={
          <ProtectedRoute module="classifications">
            <AppLayout appName="CLASSIFICATION" viewType="DetailView">
              <ClassificationDetail />
            </AppLayout>
          </ProtectedRoute>
        } />                
        {/* شاشة الأصول */}
        <Route path="/assets" element={
          <ProtectedRoute module="assets">
            <AppLayout appName="ASSET" viewType="ListView">
              <AssetsList />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* شاشة تفاصيل الأصل - لاحظ التغيير لـ DetailView */}
        <Route path="/assets/:id" element={
          <ProtectedRoute module="assets">
            <AppLayout appName="ASSET" viewType="DetailView">
              <AssetDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/meters" element={
          <ProtectedRoute module="meters">
            <AppLayout appName="METER" viewType="ListView">
              <Meters />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* شاشة تفاصيل الأصل - لاحظ التغيير لـ DetailView */}
        <Route path="/meters/:id" element={
          <ProtectedRoute module="meters">
            <AppLayout appName="METER" viewType="DetailView">
              <MeterDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/meters/new" element={
          <ProtectedRoute module="meters">
            <AppLayout appName="METER" viewType="DetailView">
              <MeterDetails />
            </AppLayout>
          </ProtectedRoute>
        } />        
        <Route path="/locations" element={
          <ProtectedRoute module="locations">
            <AppLayout appName="LOCATION" viewType="ListView">
              <Locations />
            </AppLayout>
          </ProtectedRoute>
        } />

        {/* شاشة تفاصيل الأصل - لاحظ التغيير لـ DetailView */}
        <Route path="/locations/:id" element={
          <ProtectedRoute module="locations">
            <AppLayout appName="LOCATION" viewType="DetailView">
              <LocationDetails />
            </AppLayout>
          </ProtectedRoute>
        } />        
        {/* شاشة أوامر الشغل */}
        <Route path="/workorders" element={
          <ProtectedRoute module="workorders">
            <AppLayout appName="WORKORDER" viewType="ListView">
              <WorkOrders />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/workorders/:id" element={
          <ProtectedRoute module="workorders">
            <AppLayout appName="WORKORDER" viewType="DetailView">
              <WorkOrderDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/workorders/new" element={
          <ProtectedRoute module="workorders">
            <AppLayout appName="WORKORDER" viewType="DetailView">
              <WorkOrderDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* التطبيقات المخصصة - Custom Application Builder */}
        <Route path="/custom/:slug" element={<CustomAppGuard mode="list" />} />
        <Route path="/custom/:slug/new" element={<CustomAppGuard mode="form" />} />
        <Route path="/custom/:slug/:id" element={<CustomAppGuard mode="form" />} />
        {/* JOB PLAN */}
        <Route path="/jp" element={
          <ProtectedRoute module="jp">
            <AppLayout appName="JOBPLAN" viewType="ListView">
              <JP />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/jp/:id" element={
          <ProtectedRoute module="jp">
            <AppLayout appName="JOBPLAN" viewType="DetailView">
              <JPDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/jp/new" element={
          <ProtectedRoute module="jp">
            <AppLayout appName="JOBPLAN" viewType="DetailView">
              <JPDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        {/* PM */}
        <Route path="/pm" element={
          <ProtectedRoute module="pm">
            <AppLayout appName="PM" viewType="ListView">
              <PM />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/pm/:id" element={
          <ProtectedRoute  module="pm">
            <AppLayout appName="PM" viewType="DetailView">
              <PMDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/pm/new" element={
          <ProtectedRoute  module="pm">
            <AppLayout appName="PM" viewType="DetailView">
              <PMDetails />
            </AppLayout>
          </ProtectedRoute>
        } /> 
        {/* USER */}
        <Route path="/users" element={
          <ProtectedRoute  module="users">
            <AppLayout appName="USER" viewType="ListView">
              <Users />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/users/:id" element={
          <ProtectedRoute module="users">
            <AppLayout appName="USER" viewType="DetailView">
              <UserDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/users/new" element={
          <ProtectedRoute module="users">
            <AppLayout appName="USER" viewType="DetailView">
              <UserDetails />
            </AppLayout>
          </ProtectedRoute>
        } />           
        {/* ROLE */}
        <Route path="/roles" element={
          <ProtectedRoute module="roles">
            <AppLayout appName="ROLE" viewType="ListView">
              <Roles />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/roles/:id" element={
          <ProtectedRoute module="roles">
            <AppLayout appName="ROLE" viewType="DetailView">
              <RoleDetails />
            </AppLayout>
          </ProtectedRoute>
        } />
        <Route path="/roles/new" element={
          <ProtectedRoute module="roles">
            <AppLayout appName="ROLE" viewType="DetailView">
              <RoleDetails />
            </AppLayout>
          </ProtectedRoute>
        } />    
        <Route path="/" element={<Navigate to="/dashboard" />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
      </SetupGate>
    </Router>
    </SidebarProvider>
    </FontSizeProvider>
  </AuthProvider>
  </AntdThemeBridge>
  </ThemeProvider>
  );
}

export default App;